import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AuthContext, type AuthActions, type AuthState } from './useAuth';
import { loadConfig } from '../config';
import * as auth0 from './auth0';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [tokens, setTokens] = useState<auth0.TokenBundle | null>(null);
  const initRan = useRef(false);

  useEffect(() => {
    if (initRan.current) return;
    initRan.current = true;
    (async () => {
      const config = await loadConfig();
      auth0.initAuth0(config);

      // Handle the OAuth redirect callback (Auth0 returns to /callback?code=...&state=...)
      if (window.location.pathname === '/callback' && window.location.search.includes('code=')) {
        await auth0.handleRedirectCallback();
        // Clear the query string — redirect to the stored return path or dashboard
        const returnTo = sessionStorage.getItem('auth-return-to') ?? '/dashboard';
        sessionStorage.removeItem('auth-return-to');
        window.history.replaceState({}, '', returnTo);
      }

      const cached = await auth0.getCachedSession();
      if (cached) setTokens(cached);
      setReady(true);
    })();
  }, []);

  const actions = useMemo<AuthActions>(() => ({
    async signIn(opts?: { screenHint?: 'signup' }) {
      await auth0.signIn(opts);
      // Auth0 redirects away — the rest happens on return via the callback handler above
    },
    signOut() {
      auth0.signOut();
      setTokens(null);
    },
    async getValidAccessToken() {
      if (!tokens) return null;
      const token = await auth0.getValidAccessToken();
      if (!token) {
        setTokens(null);
        return null;
      }
      return token;
    },
  }), [tokens]);

  const state: AuthState = {
    ready,
    tokens,
    email: tokens?.email ?? null,
  };

  return (
    <AuthContext.Provider value={{ ...state, ...actions }}>
      {children}
    </AuthContext.Provider>
  );
}